import { matchPath } from 'react-router-dom';

import { getWPCloudAtomicClientId } from 'lib/wpcloud-widget-runtime';
import { recordTracksEvent } from './index';

export const WPCLOUD_PORTAL_PAGE_VIEW_EVENT = 'atomic_wpcloud_partner_portal_page_view';
export const WPCLOUD_AI_ASSISTANT_INTERACTION_EVENT = 'atomic_wpcloud_ai_assistant_interaction';

type TracksPropertyValue = string | number | boolean | null | undefined;

type TrackedPageRoute = {
	matchPath: string;
	pagePath: string;
	page: string;
	section: string;
};

export type TrackedPage = Pick< TrackedPageRoute, 'page' | 'pagePath' | 'section' > & {
	sitePerformanceTab?: string;
};

export type WPCloudAIInteractionAction =
	| 'ask_synapse_event'
	| 'ask_synapse_site'
	| 'floating_assistant_open'
	| 'floating_assistant_expand'
	| 'floating_assistant_close'
	| 'message_submit';

const TRACKED_PAGE_ROUTES: TrackedPageRoute[] = [
	{
		matchPath: '/',
		pagePath: '/',
		page: 'partnerships_overview',
		section: 'partnerships',
	},
	{
		matchPath: '/wpcloud/signup',
		pagePath: '/wpcloud/signup',
		page: 'wpcloud_signup',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud/signup/pricing',
		pagePath: '/wpcloud/signup/pricing',
		page: 'wpcloud_signup_pricing',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud/billing/invoices/:invoiceId/usage-report',
		pagePath: '/wpcloud/billing/invoices/:invoice_id/usage-report',
		page: 'wpcloud_billing_invoice_usage_report',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud/billing/invoices',
		pagePath: '/wpcloud/billing/invoices',
		page: 'wpcloud_billing_invoices',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud/billing/payment-methods/add',
		pagePath: '/wpcloud/billing/payment-methods/add',
		page: 'wpcloud_billing_payment_method_add',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud/billing/payment-methods',
		pagePath: '/wpcloud/billing/payment-methods',
		page: 'wpcloud_billing_payment_methods',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud/billing/pricing',
		pagePath: '/wpcloud/billing/pricing',
		page: 'wpcloud_billing_pricing',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud/billing',
		pagePath: '/wpcloud/billing',
		page: 'wpcloud_billing_summary',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud/partnership-details',
		pagePath: '/wpcloud/partnership-details',
		page: 'wpcloud_partnership_details',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud/inventory',
		pagePath: '/wpcloud/inventory',
		page: 'wpcloud_inventory',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud/request-api-access',
		pagePath: '/wpcloud/request-api-access',
		page: 'wpcloud_request_api_access',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud/api-keys',
		pagePath: '/wpcloud/api-keys',
		page: 'wpcloud_api_keys',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud/support-request',
		pagePath: '/wpcloud/support-request',
		page: 'wpcloud_support_request',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud/site-metrics',
		pagePath: '/wpcloud/site-metrics',
		page: 'wpcloud_site_metrics',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud/assistant',
		pagePath: '/wpcloud/assistant',
		page: 'wpcloud_ai_assistant',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud/team/invite/accept',
		pagePath: '/wpcloud/team/invite/accept',
		page: 'wpcloud_team_invite_accept',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud/team',
		pagePath: '/wpcloud/team',
		page: 'wpcloud_team',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud/warden',
		pagePath: '/wpcloud/warden',
		page: 'wpcloud_warden',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud/site/:siteId/*',
		pagePath: '/wpcloud/site/:site_id/*',
		page: 'wpcloud_site_performance',
		section: 'wpcloud',
	},
	{
		matchPath: '/wpcloud',
		pagePath: '/wpcloud',
		page: 'wpcloud_overview',
		section: 'wpcloud',
	},
	{
		matchPath: '/jetpack/licenses',
		pagePath: '/jetpack/licenses',
		page: 'jetpack_licenses',
		section: 'jetpack',
	},
	{
		matchPath: '/jetpack',
		pagePath: '/jetpack',
		page: 'jetpack_account',
		section: 'jetpack',
	},
];

const SITE_PERFORMANCE_TABS = new Set( [
	'traffic',
	'caching',
	'cpu-resources',
	'database',
	'errors',
] );

function getFallbackSection( pathname: string ) {
	if ( pathname.startsWith( '/wpcloud' ) ) {
		return 'wpcloud';
	}

	if ( pathname.startsWith( '/jetpack' ) ) {
		return 'jetpack';
	}

	return 'unknown';
}

export function getTrackedPage( pathname: string ): TrackedPage {
	const route = TRACKED_PAGE_ROUTES.find( trackedRoute =>
		matchPath( { path: trackedRoute.matchPath, end: true }, pathname )
	);

	if ( route ) {
		const sitePerformanceMatch = matchPath(
			{ path: '/wpcloud/site/:siteId/:tab', end: true },
			pathname
		);
		const sitePerformanceTab = sitePerformanceMatch?.params.tab;

		return {
			page: route.page,
			pagePath: route.pagePath,
			section: route.section,
			...( sitePerformanceTab && SITE_PERFORMANCE_TABS.has( sitePerformanceTab )
				? { sitePerformanceTab }
				: {} ),
		};
	}

	return {
		page: 'unknown',
		pagePath: '/unknown',
		section: getFallbackSection( pathname ),
	};
}

export function getTracksAtomicClientId( search?: URLSearchParams ) {
	return getWPCloudAtomicClientId( search ) ?? undefined;
}

export function recordWPCloudPortalPageView(
	page: TrackedPage,
	properties: Record< string, TracksPropertyValue > = {}
) {
	recordTracksEvent( WPCLOUD_PORTAL_PAGE_VIEW_EVENT, {
		page: page.page,
		page_path: page.pagePath,
		section: page.section,
		site_performance_tab: page.sitePerformanceTab,
		...properties,
	} );
}

export function recordWPCloudAIInteraction(
	action: WPCloudAIInteractionAction,
	properties: Record< string, TracksPropertyValue > = {}
) {
	const page = getTrackedPage(
		typeof window === 'undefined' ? '' : window.location.pathname
	);

	recordTracksEvent( WPCLOUD_AI_ASSISTANT_INTERACTION_EVENT, {
		action,
		page: page.page,
		page_path: page.pagePath,
		section: page.section,
		site_performance_tab: page.sitePerformanceTab,
		atomic_client_id: getTracksAtomicClientId(),
		...properties,
	} );
}
